import { type AnyRoute, type AnyRouter, trimPathRight, useRouter } from '@tanstack/react-router';
import * as React from 'react';

import type { RouteStaticData } from '../useRouteMatches';
import type { AppLinkOptions } from './linkOptions';

type RouteChain = ReadonlyArray<Partial<RouteStaticData>>;

/**
 * The router context's `routeChainOf`, which `createAppRouter` adds: the `staticData` of each route from the root down
 * to the route a link opens, `[]` when no route has the link's `to`. The root route declares it in its context, and a
 * `beforeLoad` calls it; a component asks {@link useCanOpen} instead.
 */
export type RouteChainOf = (link: AppLinkOptions) => RouteChain;

/** Internal: the chain {@link RouteChainOf} describes, in `router`. Not exported from any barrel. */
export function routeChainWith(router: AnyRouter, link: AppLinkOptions): RouteChain {
    if (typeof link.to !== 'string') {
        return [];
    }
    // The lookup router-core's `buildLocation` does for an absolute `to`, so one with `$params` finds its route
    // without a URL.
    const routesByPath = router.routesByPath as Record<string, AnyRoute | undefined>;
    const chain: Array<Partial<RouteStaticData>> = [];
    for (let route = routesByPath[trimPathRight(link.to)]; route; route = route.parentRoute) {
        chain.push(route.options.staticData ?? {});
    }
    return chain.reverse();
}

const LinkAccessContext = React.createContext<((chain: RouteChain) => boolean) | undefined>(undefined);

/**
 * Gives {@link useCanOpen} the session's rule for who may open a page. On `router-kit`, not the main barrel: only the
 * app shell mounts it, once, and tests may.
 */
export function LinkAccessProvider({
    canOpen,
    children,
}: {
    canOpen: (chain: RouteChain) => boolean;
    children: React.ReactNode;
}): JSX.Element {
    return <LinkAccessContext.Provider value={canOpen}>{children}</LinkAccessContext.Provider>;
}

/**
 * For nav links (navbar, menus, sidebars, palette): whether the page a link opens lets the user in, so a link the
 * page would refuse isn't shown. Without a {@link LinkAccessProvider} every link passes, and the route still refuses.
 * A link without an absolute `to` passes too, here only: its {@link RouteChainOf} chain is empty.
 * A button inside a page that opens another page asks it too, as `AuthenticatedEditButton` does in
 * `CustomerLandingPageContent.tsx`; a page's predicate skips the layouts above it and the route's `modulePermissions`.
 * A `beforeLoad` can't call a hook: it uses `context.routeChainOf(link)` with the app's `canOpenRoute`.
 */
export function useCanOpen(): (link: AppLinkOptions) => boolean {
    const router = useRouter();
    const canOpen = React.useContext(LinkAccessContext);
    // No `useRouterState`: the answer depends on the route tree and the session, not on the location.
    return React.useCallback(
        (link) =>
            canOpen === undefined ||
            // No route to ask: without a `to` the link stays on the page the user is on, and a relative `to` needs
            // the location. Such a link shows, as it does without a provider.
            !link.to?.startsWith('/') ||
            canOpen(routeChainWith(router, link)),
        [router, canOpen],
    );
}
